import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StaticPage.css';

function TermsOfServicePage() {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="static-page">
      <nav className={`nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="/" className="nav-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
          <div className="nav-links">
            <a href="/">Home</a>
            <a href="/platform">Platform</a>
            <a href="/how-it-works">How it Works</a>
            <a href="/use-cases">Use Cases</a>
            <a href="/consulting">Consulting</a>
          </div>
          <div className="nav-actions">
            <button className="btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="btn-primary" onClick={() => navigate('/request-demo')}>Request a Demo</button>
          </div>
        </div>
      </nav>

      <section className="sp-hero">
        <div className="sp-container">
          <h1 className="sp-hero-title">Terms of Service</h1>
          <p className="sp-hero-text">
            Last updated: March 14, 2026
          </p>
        </div>
      </section>

      <section className="sp-section sp-legal">
        <div className="sp-container">
          <h3>1. Acceptance of Terms</h3>
          <p className="sp-text">
            By accessing or using the CerebralMachines platform, website, or services ("Services"), you agree to
            be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use our
            Services. If you are using the Services on behalf of an organization, you represent that you have the
            authority to bind that organization to these Terms.
          </p>

          <h3>2. Description of Services</h3>
          <p className="sp-text">
            CerebralMachines provides an enterprise cognitive AI platform that enables organizations to build,
            deploy, and govern semantic reasoning systems. Our Services include the platform, APIs, documentation,
            and related tools and support.
          </p>

          <h3>3. Account Registration</h3>
          <p className="sp-text">
            To access certain features, you must create an account. You agree to provide accurate, current, and
            complete information, and to keep your account credentials secure. You are responsible for all
            activity under your account.
          </p>

          <h3>4. Permitted Use</h3>
          <p className="sp-text">You may use the Services only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul className="sp-list">
            <li>Use the Services to violate any applicable law or regulation</li>
            <li>Attempt to gain unauthorized access to any part of the Services</li>
            <li>Interfere with or disrupt the Services or their infrastructure</li>
            <li>Reverse engineer, decompile, or disassemble any part of the platform</li>
            <li>Use the Services to transmit malware or harmful code</li>
            <li>Resell, sublicense, or redistribute the Services without authorization</li>
          </ul>

          <h3>5. Intellectual Property</h3>
          <p className="sp-text">
            The CerebralMachines platform, including its software, algorithms, designs, documentation, and
            trademarks, are the exclusive property of CerebralMachines, Inc. Nothing in these Terms grants you
            any right, title, or interest in our intellectual property except the limited right to use the Services
            as described herein.
          </p>

          <h3>6. Customer Data</h3>
          <p className="sp-text">
            You retain all rights to the data you upload or process through our platform ("Customer Data").
            We will not access, use, or disclose Customer Data except as necessary to provide the Services,
            comply with the law, or as otherwise described in our Privacy Policy.
          </p>

          <h3>7. Service Availability</h3>
          <p className="sp-text">
            We strive to maintain high availability of our Services but do not guarantee uninterrupted access.
            We may perform scheduled maintenance and will provide reasonable advance notice when possible.
          </p>

          <h3>8. Fees and Payment</h3>
          <p className="sp-text">
            Certain Services require payment. Fees are described in your subscription agreement or order form.
            All fees are non-refundable unless otherwise stated. We may update pricing with 30 days' notice.
          </p>

          <h3>9. Termination</h3>
          <p className="sp-text">
            Either party may terminate an account or subscription as described in the applicable agreement.
            We may suspend or terminate access immediately if you violate these Terms. Upon termination,
            your right to use the Services ceases, and we may delete your account data after a reasonable
            retention period.
          </p>

          <h3>10. Limitation of Liability</h3>
          <p className="sp-text">
            To the maximum extent permitted by law, CerebralMachines shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages arising from your use of the Services.
            Our total liability shall not exceed the fees you paid in the twelve months preceding the claim.
          </p>

          <h3>11. Disclaimer of Warranties</h3>
          <p className="sp-text">
            The Services are provided "as is" and "as available" without warranties of any kind, whether
            express or implied, including implied warranties of merchantability, fitness for a particular
            purpose, and non-infringement.
          </p>

          <h3>12. Governing Law</h3>
          <p className="sp-text">
            These Terms are governed by the laws of the State of Delaware, without regard to conflict of law
            principles. Any disputes arising from these Terms shall be resolved in the state or federal courts
            located in Delaware.
          </p>

          <h3>13. Changes to These Terms</h3>
          <p className="sp-text">
            We may update these Terms from time to time. Material changes will be communicated via email or
            a notice on our website. Continued use of the Services after changes take effect constitutes
            acceptance of the revised Terms.
          </p>

          <h3>14. Contact</h3>
          <p className="sp-text">
            For questions about these Terms of Service, please contact us at:
          </p>
          <p className="sp-emphasis">legal@cerebralmachines.ai</p>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-main">
            <div className="footer-brand">
              <a href="/" className="footer-logo" aria-label="CerebralMachines home"><img src="/CMLogo.png" alt="CerebralMachines" /></a>
              <p className="footer-tagline">Building the cognitive backbone for enterprise AI.</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <ul>
                  <li><a href="/platform">Platform</a></li>
                  <li><a href="/how-it-works">How it Works</a></li>
                  <li><a href="/use-cases">Use Cases</a></li>
                  <li><a href="/consulting">Consulting</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About</a></li>
                  <li><a href="/team">Team</a></li>
                  <li><a href="/careers">Careers</a></li>
                  <li><a href="/contact">Contact</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Legal</h4>
                <ul>
                  <li><a href="/privacy">Privacy Policy</a></li>
                  <li><a href="/terms">Terms of Service</a></li>
                  <li><a href="/security">Security</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfServicePage;
